import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import {
  LocalAgentService,
  LocalAgentCertificate,
  TokenStatusItem,
  TokenStatusResponse
} from '../../core/services/local-agent.service';
import { AgentAvailabilityService } from '../../core/services/agent-availability.service';
import { NotificationService } from '../../services/notification.service';
import { UiHelperService } from '../../services/ui-helper.service';
import { environment } from '../../../environments/environment';
import Swal from 'sweetalert2';
import { TokenContentResultModalComponent } from '../token-content-result-modal/token-content-result-modal.component';
import { TokenFisicoPinDialogComponent, TokenFisicoPinDialogResult } from '../token-fisico-pin-dialog/token-fisico-pin-dialog.component';
import {
  TokenFisicoSelectCertForResetComponent,
  TokenFisicoSelectCertForResetData,
  TokenFisicoSelectCertForResetResult
} from '../token-fisico-select-cert-for-reset/token-fisico-select-cert-for-reset.component';
import {
  TokenFisicoOtpChannelModalComponent,
  TokenFisicoOtpChannelModalData,
  TokenFisicoOtpChannelModalResult
} from '../token-fisico-otp-channel-modal/token-fisico-otp-channel-modal.component';
import {
  TokenFisicoValidateOtpModalComponent,
  TokenFisicoValidateOtpModalData,
  TokenFisicoValidateOtpModalResult
} from '../token-fisico-validate-otp-modal/token-fisico-validate-otp-modal.component';
import { maskEmail } from '../../core/utils/cert-subject.utils';

@Component({
  selector: 'app-token-fisico-admin',
  templateUrl: './token-fisico-admin.component.html',
  styleUrls: ['./token-fisico-admin.component.css'],
  standalone: false
})
export class TokenFisicoAdminComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  /** true mientras se consulta GET /api/token/status */
  statusLoading = false;
  /** true si la petición de status falló (complemento no disponible o red) */
  agentUnavailable = false;
  /** Respuesta del servicio de estado de tokens (cuando la petición fue exitosa) */
  tokenStatus: TokenStatusResponse | null = null;
  /** Certificado elegido en "Restablecer PIN" (Paso 1), para uso en Paso 2 (extraer E= del subjectDN). */
  selectedCertForResetPin: LocalAgentCertificate | null = null;

  constructor(
    private localAgentService: LocalAgentService,
    private agentAvailability: AgentAvailabilityService,
    private notificationService: NotificationService,
    private uiHelper: UiHelperService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.agentAvailability.isAvailable$.pipe(takeUntil(this.destroy$)).subscribe((ok) => {
      if (!ok) {
        this.agentUnavailable = true;
        this.statusLoading = false;
        this.tokenStatus = null;
        return;
      }
      // Si el health vuelve a estar OK y aún no tenemos estado, recargar.
      if (!this.tokenStatus && !this.statusLoading) {
        this.loadTokenStatus();
      }
    });
    this.loadTokenStatus();
  }

  ngOnDestroy(): void {
    this.uiHelper.hideSpinner();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get agentDownloadUrl(): string {
    return environment.agentDownloadUrl ?? '';
  }

  get tokens(): TokenStatusItem[] {
    return this.tokenStatus?.tokens ?? [];
  }

  get hasTokens(): boolean {
    return this.tokenStatus?.present === true && this.tokens.length > 0;
  }

  get noTokensConnected(): boolean {
    return !this.statusLoading && !this.agentUnavailable && this.tokenStatus != null && !this.hasTokens;
  }

  get showNotAvailableView(): boolean {
    return this.statusLoading || this.agentUnavailable;
  }

  get showTokenCards(): boolean {
    return !this.statusLoading && !this.agentUnavailable && this.hasTokens;
  }

  /** Llama a GET /api/token/status. Si falla, considera complemento no disponible. */
  loadTokenStatus(): void {
    this.statusLoading = true;
    this.agentUnavailable = false;
    this.tokenStatus = null;

    // Primero refrescar health compartido; si falla, no pedimos status.
    this.agentAvailability
      .refreshOnce()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ok) => {
          if (!ok) {
            this.agentUnavailable = true;
            this.statusLoading = false;
            return;
          }
          this.localAgentService
            .getTokenStatus()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res) => {
                this.tokenStatus = res;
                this.statusLoading = false;
              },
              error: () => {
                this.agentUnavailable = true;
                this.statusLoading = false;
              }
            });
        },
        error: () => {
          this.agentUnavailable = true;
          this.statusLoading = false;
        }
      });
  }

  /** Reintentar: mismo flujo que al entrar (complemento no detectado o sin tokens). */
  retryStatus(): void {
    this.loadTokenStatus();
  }

  /** Abre la URL de descarga del complemento o copia la ruta al portapapeles. */
  openAgentDownload(): void {
    const url = environment.agentDownloadUrl;
    if (url.startsWith('http')) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        Swal.fire({
          icon: 'info',
          title: 'Ruta copiada',
          html: 'La ruta del complemento se ha copiado al portapapeles.<br><br>Ábrela en el Explorador de archivos y ejecuta el instalador.',
          confirmButtonText: 'Entendido',
          ...this.notificationService.getSwalBaseConfig()
        });
      }).catch(() => this.showAgentLocalPathFallback(url));
    } else {
      this.showAgentLocalPathFallback(url);
    }
  }

  private showAgentLocalPathFallback(path: string): void {
    Swal.fire({
      icon: 'info',
      title: 'Complemento local',
      html: 'Ejecuta el instalador desde esta ruta en tu equipo:<br><br><code style="word-break:break-all;font-size:0.85em;">' + path + '</code>',
      confirmButtonText: 'Entendido',
      ...this.notificationService.getSwalBaseConfig()
    });
  }

  /** Consultar contenido: diálogo estándar PIN (botón deshabilitado hasta ≥4 caracteres) y POST /api/token/content. */
  openConsultarContenido(token: TokenStatusItem): void {
    const ref = this.dialog.open(TokenFisicoPinDialogComponent, {
      width: '420px',
      maxWidth: '95vw',
      data: { mode: 'consultar', tokenLabel: token.label, slotId: token.slotId },
      disableClose: false,
      panelClass: 'custom-dialog'
    });
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: TokenFisicoPinDialogResult | undefined) => {
      if (!result?.pin) return;
      this.uiHelper.showSpinner('Consultando contenido del token…', 'admin');
      this.localAgentService.getTokenContent(result.pin, token.slotId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.uiHelper.hideSpinner();
            if (res.success && res.certificates != null) {
              this.dialog.open(TokenContentResultModalComponent, {
                width: '640px',
                maxWidth: '95vw',
                maxHeight: '90vh',
                data: {
                  message: res.message || 'Contenido del token listado.',
                  certificates: res.certificates,
                  tokenLabel: res.tokenDetails?.label ?? token.label,
                  tokenDetails: res.tokenDetails ?? null
                },
                panelClass: 'custom-dialog'
              });
            } else {
              Swal.fire({
                icon: 'warning',
                title: 'Sin contenido',
                text: res.message || 'No se obtuvo contenido del token.',
                confirmButtonText: 'Entendido',
                ...this.notificationService.getSwalBaseConfig()
              });
            }
          },
          error: (err) => {
            this.uiHelper.hideSpinner();
            const msg = err?.error?.message || err?.message || 'No se pudo consultar el contenido del token.';
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: msg,
              confirmButtonText: 'Entendido',
              ...this.notificationService.getSwalBaseConfig()
            });
          }
        });
    });
  }

  /**
   * Restablecer PIN: Paso 0 – validar un solo token; Paso 1 – GET /api/certificates, modal elegir certificado; luego flujo PIN.
   * reset-pin usa siempre tokens[0].slotId. El certificado elegido se guarda en selectedCertForResetPin para Paso 2 (OTP).
   */
  openRestablecerPin(): void {
    this.selectedCertForResetPin = null;
    this.uiHelper.showSpinner('Detectando Certifirma Digital Físico…', 'admin');
    this.localAgentService.getTokenStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (!res.present || !res.tokens?.length) {
            this.uiHelper.hideSpinner();
            Swal.fire({
              icon: 'warning',
              title: 'No hay token conectado',
              text: 'Conecte su token e intente de nuevo.',
              confirmButtonText: 'Entendido',
              ...this.notificationService.getSwalBaseConfig()
            });
            return;
          }
          if (res.tokens.length > 1) {
            this.uiHelper.hideSpinner();
            Swal.fire({
              icon: 'warning',
              title: 'Solo un token permitido',
              text: 'Para restablecer el PIN debe tener conectado únicamente un token. Desconecte los demás y deje solo el que desea configurar; así evitamos cambiar el PIN de un token equivocado.',
              confirmButtonText: 'Entendido',
              ...this.notificationService.getSwalBaseConfig()
            });
            return;
          }
          const token = res.tokens[0];
          this.uiHelper.updateSpinnerMessage('Cargando certificados del token…', 'admin');
          this.localAgentService.getCertificates('usb_token')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (certificates) => {
                this.uiHelper.hideSpinner();
                if (!certificates?.length) {
                  Swal.fire({
                    icon: 'warning',
                    title: 'Sin certificados',
                    text: 'No hay certificados en el token USB. Conecte el token correcto e intente de nuevo.',
                    confirmButtonText: 'Entendido',
                    ...this.notificationService.getSwalBaseConfig()
                  });
                  return;
                }
                const dialogRef = this.dialog.open(TokenFisicoSelectCertForResetComponent, {
                  width: '520px',
                  maxWidth: '95vw',
                  maxHeight: '90vh',
                  data: { certificates } as TokenFisicoSelectCertForResetData,
                  disableClose: false,
                  panelClass: 'custom-dialog'
                });
                dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: TokenFisicoSelectCertForResetResult | undefined) => {
                  if (result?.certificate) {
                    this.selectedCertForResetPin = result.certificate;
                    this.openOtpChannelModalThenPinDialog(token, result.certificate);
                  }
                });
              },
              error: () => {
                this.uiHelper.hideSpinner();
                Swal.fire({
                  icon: 'error',
                  title: 'Error',
                  text: 'No se pudo obtener la lista de certificados del token.',
                  confirmButtonText: 'Entendido',
                  ...this.notificationService.getSwalBaseConfig()
                });
              }
            });
        },
        error: () => {
          this.uiHelper.hideSpinner();
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo comprobar el estado del token.',
            confirmButtonText: 'Entendido',
            ...this.notificationService.getSwalBaseConfig()
          });
        }
      });
  }

  /**
   * Abre el modal de canal OTP (correo/celular). Al "Enviar OTP" por correo: genera OTP, abre modal de validación
   * con countdown 5 min; al validar correctamente abre el diálogo de PIN. Por celular (próximamente) se usará otro endpoint.
   */
  private openOtpChannelModalThenPinDialog(token: TokenStatusItem, certificate: LocalAgentCertificate): void {
    const otpData: TokenFisicoOtpChannelModalData = {
      subjectDN: certificate.subjectDN
    };
    const ref = this.dialog.open(TokenFisicoOtpChannelModalComponent, {
      width: '480px',
      maxWidth: '95vw',
      data: otpData,
      disableClose: false,
      panelClass: 'custom-dialog'
    });
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((otpResult: TokenFisicoOtpChannelModalResult | undefined) => {
      if (!otpResult?.sendOtpRequested) return;

      if (otpResult.channel === 'email' && otpResult.transactionId && otpResult.email) {
        const validateData: TokenFisicoValidateOtpModalData = {
          transactionId: otpResult.transactionId,
          expiresInSeconds: 300,
          channel: 'email',
          maskedDestination: maskEmail(otpResult.email, 6)
        };
        const validateRef = this.dialog.open(TokenFisicoValidateOtpModalComponent, {
          width: '440px',
          maxWidth: '95vw',
          data: validateData,
          disableClose: false,
          panelClass: 'custom-dialog'
        });
        validateRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((validateResult: TokenFisicoValidateOtpModalResult | undefined) => {
          if (validateResult?.validated) {
            this.openRestablecerPinDialog(token, true);
          }
        });
        return;
      }

      this.openRestablecerPinDialog(token);
    });
  }

  /**
   * Abre el diálogo de restablecer PIN. Si afterOtp es true (tras validar OTP), solo pide nuevo PIN y confirmación;
   * si no, pide PIN actual y luego nuevo PIN. Al confirmar llama POST /api/token/reset-pin con { slotId, newUserPin }.
   */
  private openRestablecerPinDialog(token: TokenStatusItem, afterOtp = false): void {
    const ref = this.dialog.open(TokenFisicoPinDialogComponent, {
      width: '420px',
      maxWidth: '95vw',
      data: {
        mode: afterOtp ? 'restoreAfterOtp' : 'restore',
        tokenLabel: token.label,
        slotId: token.slotId
      },
      disableClose: false,
      panelClass: 'custom-dialog'
    });
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: TokenFisicoPinDialogResult | undefined) => {
      if (!result?.newPin) return;
      this.uiHelper.showSpinner('Restableciendo PIN del token…', 'admin');
      this.localAgentService.resetPin(token.slotId, result.newPin)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.uiHelper.hideSpinner();
            Swal.fire({
              icon: 'success',
              title: 'PIN restablecido',
              text: res.message || 'Ya puede usar el nuevo PIN.',
              confirmButtonText: 'Entendido',
              ...this.notificationService.getSwalBaseConfig()
            });
          },
          error: (err) => {
            this.uiHelper.hideSpinner();
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: err?.message || 'No se pudo restablecer el PIN.',
              confirmButtonText: 'Entendido',
              ...this.notificationService.getSwalBaseConfig()
            });
          }
        });
    });
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
